import React, { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FiGrid, FiList, FiSearch, FiX, FiFilter, FiChevronDown } from "react-icons/fi";
import ProductCard from "../components/product/ProductCard";
import SectionHeading from "../components/ui/SectionHeading";
import { products, categories } from "../data/products";

const SORT_OPTIONS = [
  { value: "featured", label: "Featured" },
  { value: "price-asc", label: "Price: Low to High" },
  { value: "price-desc", label: "Price: High to Low" },
  { value: "rating", label: "Highest Rated" },
  { value: "name", label: "Name A–Z" },
];

const PAGE_SIZE = 12;

const ShopPage = () => {
  const [params, setParams] = useSearchParams();
  const [view, setView] = useState("grid");
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const category = params.get("category") || "all";
  const search = params.get("search") || "";
  const sort = params.get("sort") || "featured";
  const minPrice = Number(params.get("minPrice") || 0);
  const maxPrice = Number(params.get("maxPrice") || 1000);
  const badge = params.get("badge") || "";
  const page = Number(params.get("page") || 1);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete("page");
    setParams(next);
  };

  const setPriceRange = (min, max) => {
    const next = new URLSearchParams(params);
    if (min) next.set("minPrice", min); else next.delete("minPrice");
    if (max && max < 1000) next.set("maxPrice", max); else next.delete("maxPrice");
    next.delete("page");
    setParams(next);
  };

  const filtered = useMemo(() => {
    let list = [...products];
    if (category && category !== "all") list = list.filter((p) => p.category === category);
    if (search) list = list.filter((p) => p.title.toLowerCase().includes(search.toLowerCase()) || p.shortDescription.toLowerCase().includes(search.toLowerCase()));
    if (badge) list = list.filter((p) => p.badges?.includes(badge));
    list = list.filter((p) => p.price >= minPrice && p.price <= (maxPrice || 1000));

    switch (sort) {
      case "price-asc": return list.sort((a, b) => a.price - b.price);
      case "price-desc": return list.sort((a, b) => b.price - a.price);
      case "rating": return list.sort((a, b) => b.rating - a.rating);
      case "name": return list.sort((a, b) => a.title.localeCompare(b.title));
      default: return list;
    }
  }, [category, search, sort, minPrice, maxPrice, badge]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const clearFilters = () => setParams({});

  const hasFilters = category !== "all" || search || sort !== "featured" || minPrice > 0 || maxPrice < 1000 || badge;

  const FilterPanel = () => (
    <div className="space-y-6">
      {/* Category */}
      <div>
        <h3 className="font-semibold text-gray-900 mb-3">Category</h3>
        <div className="space-y-1">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setParam("category", cat.id === "all" ? "" : cat.id)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                (cat.id === "all" && !category) || category === cat.id
                  ? "bg-brand-600 text-white font-semibold"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Price */}
      <div>
        <h3 className="font-semibold text-gray-900 mb-3">Price Range</h3>
        <div className="space-y-3">
          {[[0, 50], [50, 100], [100, 150], [150, 200], [200, 300]].map(([min, max]) => (
            <button
              key={`${min}-${max}`}
              onClick={() => setPriceRange(min, max)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                minPrice === min && maxPrice === max ? "bg-brand-600 text-white font-semibold" : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              ${min} – ${max}
            </button>
          ))}
          <button
            onClick={() => setPriceRange(200, 1000)}
            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
              minPrice === 200 && maxPrice === 1000 ? "bg-brand-600 text-white font-semibold" : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            $200+
          </button>
        </div>
      </div>

      {/* Badges */}
      <div>
        <h3 className="font-semibold text-gray-900 mb-3">Show</h3>
        <div className="space-y-1">
          {["Best Seller", "New", "Sale", "Limited Stock"].map((b) => (
            <button
              key={b}
              onClick={() => setParam("badge", badge === b ? "" : b)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                badge === b ? "bg-brand-600 text-white font-semibold" : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              {b}
            </button>
          ))}
        </div>
      </div>

      {hasFilters && (
        <button onClick={clearFilters} className="w-full btn-ghost text-sm border border-gray-200">
          <FiX size={14} /> Clear All Filters
        </button>
      )}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      {/* Header */}
      <div className="mb-8">
        <SectionHeading
          tag="StoreOnline"
          title={category && category !== "all" ? category : "All Products"}
          subtitle={`${filtered.length} product${filtered.length !== 1 ? "s" : ""} available`}
        />
      </div>

      <div className="flex gap-8">
        {/* Sidebar — desktop */}
        <aside className="hidden lg:block w-56 flex-shrink-0">
          <div className="sticky top-24">
            <FilterPanel />
          </div>
        </aside>

        {/* Main */}
        <div className="flex-1 min-w-0">
          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            {/* Search */}
            <div className="flex-1 min-w-[200px] relative">
              <FiSearch size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setParam("search", e.target.value)}
                placeholder="Search products…"
                className="input-field pl-9 py-2.5 text-sm"
              />
              {search && (
                <button onClick={() => setParam("search", "")} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <FiX size={14} />
                </button>
              )}
            </div>

            {/* Sort */}
            <div className="relative">
              <select
                value={sort}
                onChange={(e) => setParam("sort", e.target.value)}
                className="input-field py-2.5 text-sm pr-8 appearance-none cursor-pointer"
              >
                {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <FiChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>

            {/* View toggle */}
            <div className="flex border border-gray-200 rounded-xl overflow-hidden">
              <button
                onClick={() => setView("grid")}
                className={`p-2.5 transition-colors ${view === "grid" ? "bg-brand-600 text-white" : "text-gray-500 hover:bg-gray-50"}`}
                aria-label="Grid view"
              >
                <FiGrid size={16} />
              </button>
              <button
                onClick={() => setView("list")}
                className={`p-2.5 transition-colors ${view === "list" ? "bg-brand-600 text-white" : "text-gray-500 hover:bg-gray-50"}`}
                aria-label="List view"
              >
                <FiList size={16} />
              </button>
            </div>

            {/* Mobile filter toggle */}
            <button
              onClick={() => setMobileFilterOpen(true)}
              className="lg:hidden btn-ghost border border-gray-200 text-sm"
            >
              <FiFilter size={14} /> Filters
            </button>
          </div>

          {/* Active filters */}
          {hasFilters && (
            <div className="flex flex-wrap gap-2 mb-4">
              {category && category !== "all" && (
                <span className="inline-flex items-center gap-1 bg-brand-100 text-brand-700 text-xs font-medium px-3 py-1 rounded-full">
                  {category}
                  <button onClick={() => setParam("category", "")}><FiX size={12} /></button>
                </span>
              )}
              {badge && (
                <span className="inline-flex items-center gap-1 bg-brand-100 text-brand-700 text-xs font-medium px-3 py-1 rounded-full">
                  {badge}
                  <button onClick={() => setParam("badge", "")}><FiX size={12} /></button>
                </span>
              )}
              {search && (
                <span className="inline-flex items-center gap-1 bg-brand-100 text-brand-700 text-xs font-medium px-3 py-1 rounded-full">
                  "{search}"
                  <button onClick={() => setParam("search", "")}><FiX size={12} /></button>
                </span>
              )}
            </div>
          )}

          {/* Grid / List */}
          {paginated.length === 0 ? (
            <div className="text-center py-24">
              <p className="text-6xl mb-4">🔍</p>
              <h3 className="text-xl font-bold text-gray-800 mb-2">No products found</h3>
              <p className="text-gray-500 mb-6">Try adjusting your filters or search query.</p>
              <button onClick={clearFilters} className="btn-primary">Clear Filters</button>
            </div>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={`${category}-${sort}-${view}-${page}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className={
                  view === "grid"
                    ? "grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6"
                    : "flex flex-col gap-4"
                }
              >
                {paginated.map((p) => (
                  <ProductCard key={p.id} product={p} view={view} />
                ))}
              </motion.div>
            </AnimatePresence>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-12">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => { const next = new URLSearchParams(params); next.set("page", p); setParams(next); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  className={`w-10 h-10 rounded-xl font-medium text-sm transition-colors ${
                    page === p ? "bg-brand-600 text-white" : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Mobile filter drawer */}
      <AnimatePresence>
        {mobileFilterOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 z-50"
              onClick={() => setMobileFilterOpen(false)}
            />
            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="fixed left-0 top-0 h-full w-80 bg-white z-50 overflow-y-auto p-6 shadow-2xl"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-bold text-lg">Filters</h2>
                <button onClick={() => setMobileFilterOpen(false)} className="btn-ghost p-2">
                  <FiX size={20} />
                </button>
              </div>
              <FilterPanel />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ShopPage;
